import { Badge, Code, Group, Paper, Stack, Table, Text } from '@mantine/core'
import dayjs from 'dayjs'
import { cloneElement } from 'react'
import { FormattedMessage } from 'react-intl'
import { VlmResult } from '@/types/vlm'
import { formatDuration, getFormattedDateWithTime } from '@/utils/date'
import { PIPELINE_TYPE_CONFIG } from './vlmPipelineConfig'

type VlmResultDetailsTableProps = {
  result: VlmResult
}

export const VlmResultDetailsTable = ({
  result
}: VlmResultDetailsTableProps) => {
  // Null once the pipeline is deleted — the event document outlives it.
  const typeConfig = result.pipeline_source
    ? PIPELINE_TYPE_CONFIG[result.pipeline_source]
    : undefined

  return (
    <Stack gap="xs">
      <Text fw={600}>
        <FormattedMessage id="vlm.results.details.title" />
      </Text>

      <Table
        variant="vertical"
        layout="fixed"
        verticalSpacing="sm"
        horizontalSpacing="md"
        withTableBorder
      >
        <Table.Tbody>
          <Table.Tr>
            <Table.Th w={200}>
              <FormattedMessage id="vlm.results.fields.eventId" />
            </Table.Th>
            <Table.Td>
              <Code>{result.id}</Code>
            </Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Table.Th>
              <FormattedMessage id="vlm.results.fields.pipeline" />
            </Table.Th>
            <Table.Td>
              <Group gap={6} wrap="nowrap" align="center">
                {typeConfig && cloneElement(typeConfig.icon, { size: 14 })}
                <Text size="sm">
                  {result.pipeline_name ?? (
                    <FormattedMessage id="vlm.results.pipelineDeleted" />
                  )}
                </Text>
              </Group>
            </Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Table.Th w={200}>
              <FormattedMessage id="vlm.results.fields.timestamp" />
            </Table.Th>
            <Table.Td>
              <Text size="sm">
                {getFormattedDateWithTime(result.created_at)}
              </Text>
            </Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Table.Th>
              <FormattedMessage id="vlm.results.fields.duration" />
            </Table.Th>
            <Table.Td>
              <Text size="sm">
                {formatDuration(
                  dayjs(result.result.created_at).diff(
                    dayjs(result.result.timestamp)
                  )
                )}
              </Text>
            </Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Table.Th>
              <FormattedMessage id="vlm.results.fields.description" />
            </Table.Th>
            <Table.Td>
              <Paper p="sm" radius="sm" bg="gray.0" withBorder>
                <Text size="sm">{result.result.description}</Text>
              </Paper>
            </Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Table.Th>
              <FormattedMessage id="vlm.results.fields.tags" />
            </Table.Th>
            <Table.Td>
              <Group gap={4}>
                {result.result.tags.map((tag) => (
                  <Badge key={tag} size="sm" variant="light">
                    {tag}
                  </Badge>
                ))}
              </Group>
            </Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Table.Th>
              <FormattedMessage id="vlm.results.fields.alertTags" />
            </Table.Th>
            <Table.Td>
              <Group gap={4}>
                {result.result.alert_tags.map((tag) => (
                  <Badge key={tag} size="sm" variant="light" color="red">
                    {tag}
                  </Badge>
                ))}
              </Group>
            </Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </Stack>
  )
}
